import React from 'react';
import styled, { css } from 'styled-components';
import { spaces, typography } from '../../lib/styles';
import Heading from '../Heading';
import Tick1 from '../Icon/components/Tick1';

interface Props {
    items: React.ReactNode[];
    unstyled?: boolean;
    withIcon?: boolean;
    withDivider?: boolean;
    tickColor?: string;
}

interface ListHeadingProps {
    color?: string;
    children: React.ReactNode;
}

export const StyledListHeading = styled.div`
    padding-bottom: ${spaces.space8}px;
`;

const StyledList = styled.ul<{ unstyled?: boolean; withIcon?: boolean; withDivider?: boolean }>(
    ({ unstyled = false, withIcon = false, withDivider = false }) => {
        const iconListOffset = spaces.space10;

        return css`
            margin: ${spaces.typography.ul};

            ${unstyled &&
            css`
                margin: 0;
                padding: 0;
                list-style: none;
            `}

            ${withIcon &&
            css`
                ${ListItem} {
                    margin-left: ${iconListOffset}px;

                    ${withDivider &&
                    css`
                        &:before {
                            margin-left: ${-iconListOffset}px;
                            padding-right: ${iconListOffset}px;
                        }
                    `}
                }
            `}

            ${withDivider &&
            css`
                ${ListItem} {
                    &:before {
                        content: '';
                        display: block;
                        box-sizing: content-box;
                        width: 100%;
                        margin-top: 18px;
                        margin-bottom: 18px;
                        height: 1px;
                        background-color: currentColor;
                        opacity: 0.2;
                    }

                    &:first-child {
                        &:before {
                            display: none;
                        }
                    }
                }
            `}
        `;
    }
);

const ListItem = styled.li`
    font-size: ${typography.fontSizes.root}px;
    margin-top: ${spaces.space2}px;

    :first-of-type {
        margin-top: 0;
    }
`;

function List({ items, ...props }: Props) {
    return (
        <StyledList {...props}>
            {items.map((item, index) => (
                <ListItem key={index}>{item}</ListItem>
            ))}
        </StyledList>
    );
}

export const UnstyledList = function ({ items, ...props }: Props) {
    return <List {...props} items={items} unstyled={true} />;
};

const StyledHorizontalList = styled(UnstyledList)`
    display: flex;
    align-items: center;

    > li {
        margin: 0 ${spaces.space7}px;

        &:first-child {
            margin-left: 0;
        }

        &:last-child {
            margin-right: 0;
        }
    }
`;

export const HorizontalList = function ({ items, ...props }: Props) {
    return <StyledHorizontalList {...props} items={items} />;
};

const TickIcon = styled(Tick1)<{ tickColor?: string }>(({ tickColor }) => {
    return css`
        margin-left: ${-spaces.space10}px;
        margin-right: 13px;
        color: ${tickColor};
    `;
});

export const CheckList = function ({ items, tickColor, ...props }: Props) {
    return (
        <UnstyledList
            {...props}
            withIcon={true}
            items={items.map((item) => (
                <>
                    <TickIcon tickColor={tickColor} />
                    {item}
                </>
            ))}
        />
    );
};

export const ListHeading = function ({ color, children }: ListHeadingProps) {
    return (
        <StyledListHeading>
            <Heading type="h5" variations={['h5', 'no-margin']} style={{ color: color }}>
                {children}
            </Heading>
        </StyledListHeading>
    );
};

export default List;
